import { useLazyGetEmailForBugReportQuery } from 'store/services/core/settings';
import { useGetSubstitutabilityQuery } from 'store/services/core/substitutability';
import type { SvgIconComponent } from '@mui/icons-material';
import type { MouseEvent } from 'react';
import { Fragment, memo, useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { AssignmentInd, Feedback, KeyboardArrowDown, Logout, Person, SwapHoriz, Settings, Speed } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLogin } from 'contexts';
import { coreApi } from 'store/services/core';
import { documentStorageApi } from 'store/services/documentStorage';
import { flowApi } from 'store/services/flow';
import { heliosApi } from 'store/services/helios';
import { isMobileApp } from 'utils/common';
import {
  Avatar,
  GreetingAvatar,
  GreetingHeader,
  GreetingName,
  ProfileBox,
  ProfileName,
  ProfileWrapper,
  StyledMenu,
  StyledMenuItem,
  StyledMenuItemIcon,
} from './ProfileDropdown.styles';

import { useBooleanState } from 'utils/hooks';
import { UserDashboard } from 'components/user/UserDashboard';
import { useFeatureFlag } from 'utils/hooks/posthog/useFeatureFlag';

const PROFILE_DROPDOWN_TEST_IDS = {
  trigger: 'admin-user-profile-dropdown-trigger',
  menu: 'admin-user-profile-dropdown-menu',
  currentlySubstituting: 'admin-user-profile-dropdown-currently-substituting',
  profile: 'admin-user-profile-dropdown-item-profile',
  profileIcon: 'admin-user-profile-dropdown-item-profile-icon',
  userZone: 'admin-user-profile-dropdown-item-user-zone',
  userZoneIcon: 'admin-user-profile-dropdown-item-user-zone-icon',
  adminZone: 'admin-user-profile-dropdown-item-admin-zone',
  adminZoneIcon: 'admin-user-profile-dropdown-item-admin-zone-icon',
  substitutes: 'admin-user-profile-dropdown-item-substitutes',
  substitutesIcon: 'admin-user-profile-dropdown-item-substitutes-icon',
  userSettings: 'admin-user-profile-dropdown-item-user-settings',
  userSettingsIcon: 'admin-user-profile-dropdown-item-user-settings-icon',
  logout: 'admin-user-profile-dropdown-item-logout',
  logoutIcon: 'admin-user-profile-dropdown-item-logout-icon',
  feedback: 'admin-user-profile-dropdown-item-feedback',
  feedbackIcon: 'admin-user-profile-dropdown-item-feedback-icon',
} as const;

type ProfileDropdownLink = {
  Icon: SvgIconComponent;
  label: string;
  action?: () => void;
  url?: string;
  testId?: string;
  iconTestId?: string;
};

export const ProfileDropdown = memo(() => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openDashboard, toggleOpenDashboard] = useBooleanState(false);
  const { user, handleLogout, isAdmin, isImpersonating, handleUnimpersonate, isSubstituting, handleCancelSubstitution } = useLogin();
  const enabledUserDashboard = useFeatureFlag('F20-2625');
  const { pathname } = useLocation();
  const mobileApp = isMobileApp();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [getEmailForBugReport] = useLazyGetEmailForBugReportQuery();
  const { data: { isVisible: isSubstitutabilityVisible = false } = {} } = useGetSubstitutabilityQuery();

  const resetCache = useCallback(() => {
    dispatch(coreApi.util.resetApiState());
    dispatch(flowApi.util.resetApiState());
    dispatch(heliosApi.util.resetApiState());
    dispatch(documentStorageApi.util.resetApiState());
  }, [dispatch]);

  const handleSendFeedback = useCallback(async () => {
    const { data } = await getEmailForBugReport();
    window.open(`mailto:${data?.email}`);
  }, [getEmailForBugReport]);

  const handleNavigateToSubstitutes = useCallback(() => {
    resetCache();
    navigate('/user/substitutes');
  }, [navigate, resetCache]);

  const handleNavigateToUserSettings = useCallback(() => {
    resetCache();
    navigate('/user/settings');
  }, [navigate, resetCache]);

  const handleNavigateToProfile = useCallback(() => {
    resetCache();
    navigate('/admin/core/users/' + user?.user_id);
  }, [navigate, resetCache, user?.user_id]);

  const handleNavigateToUserProfile = useCallback(() => {
    resetCache();
    navigate('/user/profile/');
  }, [navigate, resetCache]);

  const handleOpenMenu = useCallback((event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget), []);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const isOnAdminSite = useMemo(() => pathname.includes('/admin'), [pathname]);

  const showSubstitutability = useMemo(
    () => isSubstitutabilityVisible && !isSubstituting && !isImpersonating && !mobileApp,
    [isSubstitutabilityVisible, isSubstituting, isImpersonating, mobileApp]
  );

  const links: Array<ProfileDropdownLink | null> = [
    isImpersonating
      ? {
          label: t('core.users.cancel_impersonate'),
          Icon: AssignmentInd,
          action: handleUnimpersonate,
        }
      : null,
    isSubstituting
      ? {
          label: t('core.users.cancel_substitution'),
          Icon: SwapHoriz,
          action: handleCancelSubstitution,
        }
      : null,
    isAdmin && !mobileApp
      ? {
          label: t('sidebar.profile'),
          Icon: Person,
          action: handleNavigateToProfile,
          testId: PROFILE_DROPDOWN_TEST_IDS.profile,
          iconTestId: PROFILE_DROPDOWN_TEST_IDS.profileIcon,
        }
      : {
          label: t('sidebar.profile'),
          Icon: Person,
          action: handleNavigateToUserProfile,
          testId: PROFILE_DROPDOWN_TEST_IDS.profile,
          iconTestId: PROFILE_DROPDOWN_TEST_IDS.profileIcon,
        },
    isOnAdminSite
      ? {
          label: t('sidebar.userZone'),
          Icon: AssignmentInd,
          url: '/user',
          testId: PROFILE_DROPDOWN_TEST_IDS.userZone,
          iconTestId: PROFILE_DROPDOWN_TEST_IDS.userZoneIcon,
        }
      : isAdmin && !mobileApp
        ? {
            label: t('sidebar.adminZone'),
            Icon: AssignmentInd,
            url: '/admin',
            testId: PROFILE_DROPDOWN_TEST_IDS.adminZone,
            iconTestId: PROFILE_DROPDOWN_TEST_IDS.adminZoneIcon,
          }
        : null,

    showSubstitutability
      ? {
          label: t('sidebar.substitutes'),
          Icon: SwapHoriz,
          action: handleNavigateToSubstitutes,
          testId: PROFILE_DROPDOWN_TEST_IDS.substitutes,
          iconTestId: PROFILE_DROPDOWN_TEST_IDS.substitutesIcon,
        }
      : null,
    {
      Icon: Settings,
      label: t('sidebar.userSettings'),
      action: handleNavigateToUserSettings,
      testId: PROFILE_DROPDOWN_TEST_IDS.userSettings,
      iconTestId: PROFILE_DROPDOWN_TEST_IDS.userSettingsIcon,
    },
    {
      Icon: Logout,
      label: t('sidebar.logout'),
      action: handleLogout,
      testId: PROFILE_DROPDOWN_TEST_IDS.logout,
      iconTestId: PROFILE_DROPDOWN_TEST_IDS.logoutIcon,
    },
    {
      Icon: Feedback,
      label: t('sidebar.feedback'),
      action: handleSendFeedback,
      testId: PROFILE_DROPDOWN_TEST_IDS.feedback,
      iconTestId: PROFILE_DROPDOWN_TEST_IDS.feedbackIcon,
    },
    !isOnAdminSite && enabledUserDashboard
      ? {
          Icon: Speed,
          label: t('core.settings.dashboard.placeholder'),
          action: toggleOpenDashboard,
        }
      : null,
  ];

  return (
    <div>
      <ProfileBox
        isSubstituting={isSubstituting}
        onClick={handleOpenMenu}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setAnchorEl(event.currentTarget);
          }
        }}
        tabIndex={0}
        role="button"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        aria-controls={anchorEl ? PROFILE_DROPDOWN_TEST_IDS.menu : undefined}
        data-testid={PROFILE_DROPDOWN_TEST_IDS.trigger}>
        <ProfileWrapper>
          <Avatar data-testid={`${PROFILE_DROPDOWN_TEST_IDS.trigger}-avatar`}>
            <Person />
          </Avatar>
          <ProfileName data-testid={`${PROFILE_DROPDOWN_TEST_IDS.trigger}-email`}>{user?.email}</ProfileName>
        </ProfileWrapper>
        <KeyboardArrowDown sx={{ color: 'text.secondary', fontSize: 18 }} />
      </ProfileBox>

      <StyledMenu
        anchorEl={anchorEl}
        disableScrollLock
        open={Boolean(anchorEl)}
        onClose={handleClose}
        id={PROFILE_DROPDOWN_TEST_IDS.menu}
        data-testid={PROFILE_DROPDOWN_TEST_IDS.menu}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: anchorEl ? `${anchorEl.offsetWidth}px` : 'auto',
            },
          },
        }}>
        <GreetingHeader>
          <GreetingAvatar>{(user?.full_name ?? '?').charAt(0).toUpperCase()}</GreetingAvatar>
          <GreetingName>{user?.full_name}</GreetingName>
        </GreetingHeader>
        {isSubstituting && (
          <StyledMenuItem
            data-testid={PROFILE_DROPDOWN_TEST_IDS.currentlySubstituting}
            disabled
            sx={{
              opacity: 1,
              backgroundColor: 'transparent',
              cursor: 'default',
              '&:hover': {
                backgroundColor: 'transparent',
              },
              '&.Mui-disabled': {
                opacity: 1,
              },
            }}>
            <Typography variant="inherit" sx={{ fontWeight: 600 }}>
              {t('core.users.currently_substituting')} {user?.full_name}
            </Typography>
          </StyledMenuItem>
        )}
        {links.map((link) => {
          if (!link) return null;
          const { Icon, action, url, label } = link;

          const menuItem = (
            <StyledMenuItem
              data-testid={link.testId}
              onClick={
                action
                  ? () => {
                      handleClose();
                      action();
                    }
                  : url
                    ? () => {
                        handleClose();
                      }
                    : undefined
              }>
              <StyledMenuItemIcon data-testid={link.iconTestId}>
                <Icon />
              </StyledMenuItemIcon>
              <Typography variant="inherit">{label}</Typography>
            </StyledMenuItem>
          );

          if (url) {
            return (
              <Link key={label} to={url} reloadDocument>
                {menuItem}
              </Link>
            );
          }

          return <Fragment key={label}>{menuItem}</Fragment>;
        })}
      </StyledMenu>
      {openDashboard && <UserDashboard open={openDashboard} onClose={toggleOpenDashboard} />}
    </div>
  );
});
